import { createContext, Dispatch, SetStateAction, useEffect, useMemo, useState, ReactNode, useContext } from 'react'
import { useLocation } from 'react-router-dom'
import useApi, { ApiResponse } from 'hooks/useApi'
import { createCsApiUrlFromEndpoint, getEntityIdFromUrl } from 'utils'
import appContext from 'context/appContext'
import type { productTabsIds } from 'components/Tabs/tabs-type'
import usePermissions from 'hooks/usePermissions'
import ProjectPageContext from 'context/projectContext/projectContext'
import type { ProductsDetailDataV5 } from 'utils/utils.d'

interface ProductContextProps {
  productID: string | undefined
  setProductID: Dispatch<SetStateAction<string | undefined>>
  productAPI: ApiResponse<ProductsDetailDataV5 | null>
  currentTab: productTabsIds
  setCurrentTab: Dispatch<SetStateAction<productTabsIds>>
}

export const ProductContext = createContext<ProductContextProps>({
  productID: undefined,
  setProductID: () => {},
  productAPI: {
    data: null,
    error: null,
    isLoading: false,
    fetchData: () => Promise.resolve(null),
    url: null!,
    setUrl: function (): void {
      throw new Error('Function not implemented.')
    },
  },
  currentTab: 'product_data',
  setCurrentTab: () => {},
})

interface ProductProviderProps {
  children: ReactNode
}
export const ProductProvider = ({ children }: ProductProviderProps) => {
  const [productID, setProductID] = useState<string>()
  const { permissions: projectPermissions, clientDetails } = useContext(ProjectPageContext)
  const [currentTab, setCurrentTab] = useState<productTabsIds>('product_data')
  const productURL = useMemo(() => createCsApiUrlFromEndpoint(`products/${productID}`), [productID])
  const productAPI = useApi<ProductsDetailDataV5>(productURL)
  const { osContextValue } = useContext(appContext)
  const { fetchData: getProductData, setUrl: setGetProductUrl } = productAPI
  const location = useLocation()
  const clientId = clientDetails?.id
  const { clientLevelPermissions, isGetPermissionsLoading } = usePermissions({
    clientId,
    permissionSubset: ['PDP_APP_GET_PRODUCTS_BY', 'PDP_APP_DELETE_PRODUCTS_BY', 'PDP_APP_GET_PRODUCTS_EXPORT'],
  })
  const [canGetProduct] = clientLevelPermissions

  useEffect(() => {
    if (productID) return
    const productIDFromUrl = getEntityIdFromUrl(location.pathname, 'product', {
      baseUrl: osContextValue?.osContext?.baseUrl || '',
    })

    if (productIDFromUrl) {
      setProductID(productIDFromUrl)
    }
  }, [])

  useEffect(() => {
    if (!productID || isGetPermissionsLoading || !projectPermissions.canGetProject || !canGetProduct) return

    setGetProductUrl(productURL)
    getProductData(productURL).catch(console.error)
  }, [productID, isGetPermissionsLoading, projectPermissions.canGetProject, canGetProduct])

  return (
    <ProductContext.Provider value={{ productID, setProductID, productAPI, currentTab, setCurrentTab }}>
      {children}
    </ProductContext.Provider>
  )
}
